import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Check, AlertCircle, ChevronRight } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
type Role = 'learner' | 'parent' | 'teacher';
interface RegisterNameStepProps {
  role: Role;
  name: string;
  onNameChange: (value: string) => void;
  usernameAvailable: boolean;
  suggestedUsername: string;
  /** Alternatieven wanneer de kale voornaam bezet is. */
  suggestions: string[];
  /** Kind kiest een alternatief. */
  onPickSuggestion: (username: string) => void;
  /** Gekozen alternatief, of '' als het kind de voornaam gebruikt. */
  chosenUsername: string;
  /**
   * De gebruikersnaam die de server zojuist weigerde.
   *
   * Los van `suggestedUsername`, want die wordt uit de voornaam afgeleid.
   * Koos het kind eerder `pietje-tijger` en is dát bezet, dan moet de melding
   * die naam noemen — anders wijst hij een naam aan die niets mankeert.
   */
  takenUsername?: string;
  slideVariants: Record<string, unknown>;
}
export function RegisterNameStep({
  role,
  name,
  onNameChange,
  usernameAvailable,
  suggestedUsername,
  suggestions,
  onPickSuggestion,
  chosenUsername,
  takenUsername,
  slideVariants
}: RegisterNameStepProps) {
  // Zodra het kind een alternatief heeft gekozen, is er niets meer bezet en
  // hoeft het formulier niet langer te waarschuwen.
  const resolved = chosenUsername || (usernameAvailable ? suggestedUsername : '');
  const needsChoice = !resolved;
  return <motion.div key="step-name" custom={1} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
    duration: 0.3
  }} className="space-y-5" data-id="element-397" data-mp="2sh8xsy9asy6o">
      
      <h2 className="text-xl font-black text-slate-900 text-center" data-id="element-398" data-mp="2ins37s1c7f1y">
        {role === 'learner' ? 'Hoe heet je?' : 'Wat is je naam?'}
      </h2>

      <div data-id="element-399" data-mp="3b5e1k5pygj5h">
        <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-400" data-mp="3b5w4fl74hvmd">
          {role === 'learner' ? 'Voornaam' : 'Volledige naam'}
        </label>
        <div className="relative" data-id="element-401" data-mp="3kziftufd9rjy">
          <Input type="text" required autoFocus value={name} onChange={e => onNameChange(e.target.value)} icon={<User className="w-5 h-5" data-id="element-403" data-mp="3c0ewcpcafa4x" />} className="bg-slate-50 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 text-lg font-medium py-4" placeholder={role === 'learner' ? 'bijv. Djayden' : 'bijv. Jan de Vries'} data-id="element-402" data-mp="3gzhstle7c81a" />
          
        </div>
      </div>

      {/* Username preview for learners */}
      <AnimatePresence data-id="element-404" data-mp="2xaz7jewbherv">
        {role === 'learner' && name.trim() && <motion.div initial={{
        opacity: 0,
        y: -10,
        height: 0
      }} animate={{
        opacity: 1,
        y: 0,
        height: 'auto'
      }} exit={{
        opacity: 0,
        y: -10,
        height: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-405" data-mp="2ta6zuq33fhxp">
          
            <div className={`p-4 rounded-2xl border mt-2 ${needsChoice ? 'bg-amber-50 border-amber-200' : 'bg-orange-50 border-orange-100'}`} data-id="element-406" data-mp="2ee7gukeg3xds">
            
              <div className="flex items-start gap-3" data-id="element-407" data-mp="298c4bzbilg2n">
                <div className={`mt-0.5 ${needsChoice ? 'text-amber-500' : 'text-orange-500'}`} data-id="element-408" data-mp="2970yzv2f809f">
                
                  {needsChoice ? <AlertCircle className="w-5 h-5" data-id="element-409" data-mp="2e5cdwk7kp0yi" /> : <Check className="w-5 h-5" data-id="element-410" data-mp="2j71ndvf5d6kh" />}
                </div>
                <div className="min-w-0" data-id="element-411" data-mp="2j45p25ze0fvi">
                  <p className={`text-sm font-medium ${needsChoice ? 'text-amber-900' : 'text-orange-800'}`} data-id="element-412" data-mp="1ljsw1jsgt3ak">
                  
                    {needsChoice ? `Er is al iemand die ${takenUsername || suggestedUsername} heet. Kies er een:` : 'Je gebruikersnaam wordt:'}
                  </p>

                  {!needsChoice && <>
                      <p className="text-lg font-black mt-0.5 text-orange-600 break-all" data-id="element-413" data-mp="208ywfb31gzk1">
                        {resolved}
                      </p>
                      <p className="text-xs mt-1 text-orange-600/70" data-id="element-414" data-mp="17qjqju7n11xb">
                        Hiermee log je straks in. Onthoud hem goed!
                      </p>
                    </>}
                </div>
              </div>

              {/* Kiezen kan een kind van negen wél; verzinnen niet. Daarom
                 voorstellen in plaats van "probeer iets anders" — en bewust
                 geen suggestie om een achternaam toe te voegen: dat maakt een
                 pseudonieme naam herleidbaar tot een persoon, en die staat
                 daarna op het inlogscherm van een gedeeld apparaat. */}
              {needsChoice && <div className="mt-3 flex flex-wrap gap-2" data-id="element-415" data-mp="3o4n64b97yerm">
                  {suggestions.map(suggestion => <button key={suggestion} type="button" onClick={() => onPickSuggestion(suggestion)} className="px-3 py-2 rounded-xl bg-white border-2 border-amber-300 text-amber-900 font-bold text-sm hover:border-orange-400 hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-colors" data-id="element-416" data-mp="5yqunqo7hf9l">
                
                      {suggestion}
                    </button>)}
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>

      <Button type="submit" variant="primary" size="lg" className="w-full" disabled={!name.trim() || role === 'learner' && !usernameAvailable} data-id="element-417" data-mp="n9e0tjxa3zo3">
        
        Verder <ChevronRight className="w-5 h-5 ml-1" data-id="element-418" data-mp="8idsdewj85ry" />
      </Button>
    </motion.div>;
}